import { InputWithSuffix } from '@components/ui/input-group'
import { Slider } from '@components/ui/slider'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'
import { cn } from '@/utils'
import type { EffectSliderBindings } from '../types/effect-slider-bindings'

interface EffectSliderFieldProps extends EffectSliderBindings {
  label: string
  /** Accessible name of the numeric field. */
  ariaLabel: string
  /** DOM id of the inline error message. */
  errorId: string
  /** Unit shown inside the field (e.g. `%`); omitted renders a plain input. */
  suffix?: string
}

/**
 * One numeric Object Effects control: label, preset row, slider and a commit-on-leave text field
 * with its inline range error underneath.
 */
export function EffectSliderField({
  label,
  ariaLabel,
  errorId,
  suffix,
  presets,
  selectedPreset,
  onPresetChange,
  sliderValue,
  min,
  max,
  onSliderChange,
  onSliderCommit,
  onSessionStart,
  inputText,
  inputHandlers,
  error,
  disabled,
  dimmed,
}: EffectSliderFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-foreground leading-normal">{label}</label>
      </div>
      <div className={cn(dimmed && 'pointer-events-none opacity-50')}>
        <ToggleGroup
          type="single"
          value={selectedPreset || ''}
          onValueChange={onPresetChange}
          className="justify-start gap-1"
          disabled={disabled}
        >
          {presets.map((preset) => (
            <ToggleGroupItem
              key={preset.label}
              value={preset.label}
              className="h-8 px-2 min-w-[2.5rem] text-xs font-medium"
            >
              {preset.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div
        className={cn('flex min-w-0 flex-col gap-0.5', dimmed && 'pointer-events-none opacity-50')}
        onPointerDownCapture={onSessionStart}
      >
        <div className="flex min-w-0 items-center gap-[18px]">
          <Slider
            value={sliderValue}
            onValueChange={onSliderChange}
            onValueCommit={onSliderCommit}
            min={min}
            max={max}
            step={1}
            className="min-w-0 flex-1"
            disabled={disabled}
          />
          <InputWithSuffix
            type="text"
            inputMode="numeric"
            value={inputText}
            onFocus={inputHandlers.onFocus}
            onChange={inputHandlers.onChange}
            onBlur={inputHandlers.onBlur}
            onKeyDown={inputHandlers.onKeyDown}
            size="compact"
            disabled={disabled}
            error={!!error}
            className="tabular-nums"
            placeholder="Value"
            aria-label={ariaLabel}
            aria-describedby={error ? errorId : undefined}
            suffix={
              suffix ? (
                <span className="text-xs font-medium text-muted-foreground">{suffix}</span>
              ) : undefined
            }
          />
        </div>
        {error ? (
          <div className="flex min-w-0 gap-[18px]">
            <div className="min-w-0 flex-1" aria-hidden />
            <div className="w-[80px] shrink-0 pt-0.5 sm:w-[100px]" id={errorId} role="alert">
              <span className="text-xs text-destructive leading-tight block">{error}</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
